import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import WhatsAppButton from "../../components/feature/WhatsAppButton";

const values = [
  {
    icon: "ri-shield-check-line",
    title: "Integrity",
    desc: "We do what we say. Every promise we make to our clients is a commitment we keep, from punctuality to quality.",
  },
  {
    icon: "ri-sparkling-2-line",
    title: "Excellence",
    desc: "Good isn't good enough. We chase perfection in every corner, every surface, every service we deliver.",
  },
  {
    icon: "ri-heart-line",
    title: "Care",
    desc: "We treat your facility as if it were our own — with respect, attention, and genuine care for every detail.",
  },
  {
    icon: "ri-leaf-line",
    title: "Sustainability",
    desc: "We use eco-friendly products that protect your staff, your clients, and the environment we share.",
  },
];

const certifications = [
  { icon: "ri-shield-star-fill", title: "Fully Licensed & Insured", sub: "Licensed, bonded & insured in WA State" },
  { icon: "ri-government-line", title: "OSHA Compliant", sub: "All processes meet federal safety standards" },
  { icon: "ri-leaf-fill", title: "EPA Approved Products", sub: "Only certified eco-safe cleaning agents" },
  { icon: "ri-user-star-fill", title: "Background-Checked Staff", sub: "Every technician is thoroughly vetted" },
  { icon: "ri-medal-2-fill", title: "10+ Years Experience", sub: "Serving the greater Seattle area since 2014" },
  { icon: "ri-thumb-up-fill", title: "Satisfaction Guarantee", sub: "100% satisfaction or we come back — free" },
];

const whyUs = [
  { icon: "ri-verified-badge-fill", title: "Licensed & Insured", desc: "Fully licensed, bonded and insured. You're always protected." },
  { icon: "ri-user-star-line", title: "Background-Checked Staff", desc: "Every team member passes thorough background checks before joining." },
  { icon: "ri-recycle-line", title: "Eco-Friendly Products", desc: "Safe for your clients, employees, and the environment. Premium quality only." },
  { icon: "ri-customer-service-2-line", title: "Responsive Support", desc: "We respond to all inquiries within a few hours, always." },
  { icon: "ri-price-tag-3-line", title: "Transparent Pricing", desc: "No surprises. We quote upfront and stick to it, always." },
  { icon: "ri-repeat-2-line", title: "Consistent Quality", desc: "Our checklists and quality control ensure the same high standard every visit." },
];

const AboutPage = () => {
  const { t } = useTranslation();
  const storyRef = useRef<HTMLDivElement>(null);
  const valuesRef = useRef<HTMLDivElement>(null);
  const certsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".animate-entry");
            items.forEach((item, i) => {
              setTimeout(() => {
                item.classList.add("opacity-100", "translate-y-0");
                item.classList.remove("opacity-0", "translate-y-8");
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (storyRef.current) observer.observe(storyRef.current);
    if (valuesRef.current) observer.observe(valuesRef.current);
    if (certsRef.current) observer.observe(certsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-[#0F2A44] overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20commercial%20office%20building%20interior%2C%20polished%20marble%20floors%2C%20floor%20to%20ceiling%20glass%20windows%2C%20corporate%20professional%20environment%2C%20deep%20navy%20blue%20tones%2C%20elegant%20premium%20atmosphere%2C%20abstract%20architectural%20background%20texture&width=1920&height=600&seq=aboutbg_commercial001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-4 block">
            {t("about_label")}
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5">
            {t("about_headline")}
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            10+ years delivering premium professional cleaning services to offices, clinics, banks, and commercial facilities across Washington State.
          </p>
          <div className="flex justify-center mt-8 text-sm text-white/50">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors duration-200">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-[#D4AF37]">About</span>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6" ref={storyRef}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-entry opacity-0 translate-y-8 transition-all duration-700">
              <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-3 block">
                {t("about_story_label")}
              </span>
              <h2 className="text-4xl font-bold text-[#0F2A44] mb-5">
                {t("about_story_headline")}
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mb-5">
                Prime Shine WA was founded in Seattle with one clear mission: to bring world-class commercial cleaning standards to every business, clinic, office, and facility in Washington State. What started as a focused commercial cleaning operation quickly grew into one of the region's most trusted professional cleaning companies.
              </p>
              <p className="text-gray-500 text-base leading-relaxed mb-5">
                With over 10 years of experience, we've built our reputation on one simple belief — every business deserves a spotless, healthy, and welcoming environment. Today, we serve offices, medical clinics, banks, car dealerships, and commercial facilities across the greater Seattle area.
              </p>
              <p className="text-gray-500 text-base leading-relaxed mb-8">
                Our team isn't just trained to clean — they're trained to care about every detail, every surface, every time. That's the Prime Shine standard.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { num: "500+", label: "Business Clients" },
                  { num: "10+", label: "Years Experience" },
                  { num: "100%", label: "Satisfaction Rate" },
                  { num: "20+", label: "Team Members" },
                ].map((stat) => (
                  <div key={stat.label} className="bg-[#F5F5F5] rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-[#D4AF37]">{stat.num}</p>
                    <p className="text-xs text-gray-500 font-medium mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="animate-entry opacity-0 translate-y-8 transition-all duration-700 delay-200">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20commercial%20cleaning%20team%20in%20dark%20navy%20uniforms%20working%20in%20a%20spotless%20modern%20corporate%20office%20building%20lobby%2C%20polished%20marble%20floors%2C%20glass%20walls%2C%20gleaming%20surfaces%2C%20premium%20commercial%20photography%2C%20Seattle%20Washington%20business%20environment&width=700&height=550&seq=about_commercial001&orientation=landscape"
                  alt="Prime Shine WA commercial cleaning team"
                  className="w-full h-96 object-cover object-top rounded-2xl"
                  loading="lazy"
                />
                <div
                  className="absolute -bottom-6 -left-6 bg-[#0F2A44] text-white rounded-2xl p-5"
                  style={{ boxShadow: "0 8px 30px rgba(15,42,68,0.3)" }}
                >
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className="ri-star-fill text-[#D4AF37] text-xs" />
                    ))}
                  </div>
                  <p className="text-sm font-bold">4.9/5 Average Rating</p>
                  <p className="text-xs text-white/60">247+ Google Reviews</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-10" style={{ boxShadow: "0 2px 20px rgba(0,0,0,0.06)" }}>
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#D4AF37]/15 mb-5">
                <i className="ri-focus-3-line text-xl text-[#D4AF37]" />
              </div>
              <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-2 block">
                {t("about_mission_label")}
              </span>
              <h2 className="text-2xl font-bold text-[#0F2A44] mb-4">
                {t("about_mission_headline")}
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                Our mission is to deliver exceptional, reliable, and eco-conscious commercial cleaning services that create safer, healthier environments for every business we serve. We commit to showing up on time, cleaning with precision, and leaving every facility better than we found it — every single time.
              </p>
            </div>

            <div className="bg-[#0F2A44] rounded-2xl p-10">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#D4AF37]/20 mb-5">
                <i className="ri-eye-line text-xl text-[#D4AF37]" />
              </div>
              <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-2 block">
                {t("about_vision_label")}
              </span>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t("about_vision_headline")}
              </h2>
              <p className="text-white/65 text-sm leading-relaxed">
                We envision a Washington State where every office, clinic, bank, and commercial facility has access to premium, professional cleaning services. We strive to be the most trusted commercial cleaning brand in the Pacific Northwest — known for our reliability, our people, and our unwavering commitment to quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white" ref={valuesRef}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-3 block">
              {t("about_values_label")}
            </span>
            <h2 className="text-4xl font-bold text-[#0F2A44]">
              {t("about_values_headline")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div
                key={v.title}
                className="animate-entry opacity-0 translate-y-8 transition-all duration-500 text-center p-8 rounded-2xl bg-[#F5F5F5] hover:bg-[#D4AF37]/5"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#D4AF37]/15 mx-auto mb-5">
                  <i className={`${v.icon} text-2xl text-[#D4AF37]`} />
                </div>
                <h3 className="text-lg font-bold text-[#0F2A44] mb-3">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#0F2A44]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-3 block">
              {t("about_why_label")}
            </span>
            <h2 className="text-4xl font-bold text-white">{t("about_why_headline")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyUs.map((w) => (
              <div
                key={w.title}
                className="flex items-start gap-5 p-6 rounded-2xl border border-white/10 hover:border-[#D4AF37]/30 hover:bg-white/5 transition-all duration-200"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#D4AF37]/15 shrink-0">
                  <i className={`${w.icon} text-xl text-[#D4AF37]`} />
                </div>
                <div>
                  <h3 className="text-white font-bold text-base mb-1.5">{w.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Standards (replaces Team section) */}
      <section className="py-20 bg-white" ref={certsRef}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-3 block">
              OUR CREDENTIALS
            </span>
            <h2 className="text-4xl font-bold text-[#0F2A44]">
              Certified. Compliant. Trusted.
            </h2>
            <p className="text-gray-500 text-base mt-4 max-w-xl mx-auto">
              Every standard we meet is a promise we keep to our clients — from safety compliance to eco-responsible practices.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert) => (
              <div
                key={cert.title}
                className="animate-entry opacity-0 translate-y-8 transition-all duration-500 flex items-center gap-5 p-6 rounded-2xl bg-[#F5F5F5] hover:bg-[#D4AF37]/5 transition-colors duration-200 border border-transparent hover:border-[#D4AF37]/20"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#0F2A44] shrink-0">
                  <i className={`${cert.icon} text-2xl text-[#D4AF37]`} />
                </div>
                <div>
                  <h3 className="text-[#0F2A44] font-bold text-base mb-1">{cert.title}</h3>
                  <p className="text-gray-500 text-sm leading-snug">{cert.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Commercial image bar */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { src: "https://readdy.ai/api/search-image?query=spotless%20modern%20medical%20clinic%20hallway%20with%20shiny%20floors%20and%20sterile%20white%20walls%2C%20professional%20commercial%20cleaning%20result%2C%20bright%20fluorescent%20lighting%2C%20healthcare%20facility%20cleanliness%2C%20premium%20medical%20environment%20photography&width=500&height=300&seq=about_clinic001&orientation=landscape", alt: "Medical clinic cleaning" },
              { src: "https://readdy.ai/api/search-image?query=gleaming%20corporate%20bank%20interior%20with%20polished%20marble%20floors%2C%20clean%20teller%20counters%2C%20spotless%20glass%20partitions%2C%20professional%20commercial%20cleaning%20result%2C%20elegant%20financial%20institution%20environment%2C%20premium%20photography&width=500&height=300&seq=about_bank001&orientation=landscape", alt: "Bank cleaning services" },
              { src: "https://readdy.ai/api/search-image?query=pristine%20car%20dealership%20showroom%20floor%20with%20gleaming%20luxury%20vehicles%20on%20polished%20epoxy%20floor%2C%20spotless%20glass%20walls%2C%20professional%20commercial%20cleaning%2C%20modern%20automotive%20showroom%20interior%20photography&width=500&height=300&seq=about_dealer001&orientation=landscape", alt: "Car dealership cleaning" },
            ].map((img) => (
              <div key={img.alt} className="rounded-2xl overflow-hidden h-44">
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-[#0F2A44] to-[#1a4a7a] py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Experience the Prime Shine Difference?
          </h2>
          <p className="text-white/65 mb-8">
            Join hundreds of satisfied clients who trust us with their most important commercial spaces.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8952E] text-[#0F2A44] font-bold px-10 py-4 rounded-full text-base transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            {t("nav_cta")}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default AboutPage;
